import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "app-tooltip" }
const _hoisted_2 = ["innerHTML"]

import {
	ref,
	computed,
	watch,
	onMounted,
	onUnmounted,
	nextTick,
	type StyleValue
} from 'vue'
import { useBreakpoints } from '@/composables/useBreakpoints'

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right'
type TooltipAnchorAlign = 'start' | 'center' | 'end'
type TooltipAnchorRect = Pick<DOMRect, 'top' | 'bottom' | 'left' | 'right' | 'width' | 'height'>

type AppTooltipProps = {
	position?: TooltipPosition
	showArrow?: boolean
	text: string
	maxWidth?: string
	showByActivatorClick?: boolean,
	closeByClickDelay?: number,
	delay?: number,
	disabled?: boolean,
	textAlign?: 'left' | 'center' | 'right',
	followCursor?: boolean,
	offset?: number,
	anchorAlign?: TooltipAnchorAlign,
	closeOnActivatorMouseLeave?: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AppTooltip',
  props: {
    position: { default: 'top' },
    showArrow: { type: Boolean, default: false },
    text: {},
    maxWidth: { default: 'none' },
    showByActivatorClick: { type: Boolean, default: false },
    closeByClickDelay: { default: 3000 },
    delay: { default: 300 },
    disabled: { type: Boolean, default: false },
    textAlign: { default: 'center' },
    followCursor: { type: Boolean, default: false },
    offset: {},
    anchorAlign: { default: 'center' },
    closeOnActivatorMouseLeave: { type: Boolean, default: false }
  },
  setup(__props: any, { expose: __expose }) {

const props = __props

const { isMobile, isTablet } = useBreakpoints()

const isVisible = ref(false)
const tooltipRef = ref<HTMLElement | null>(null)
const activatorRef = ref<HTMLElement | null>(null)

const computedPosition = ref(props.position)

// Без ограничения ширины подсказка идёт одной строкой, как в остальных подсказках проекта.
// Как только ширину задали, текст должен переноситься, иначе он вылезет за рамку.
const isWidthLimited = computed(() => props.maxWidth !== 'none')

const tooltipPosition = ref({ top: 0, left: 0 })
const arrowOffset = ref(0)
const delayTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const closeTimer = ref<ReturnType<typeof setTimeout> | null>(null)

// Последняя известная позиция курсора над активатором (клиентские координаты)
const cursorPosition = ref<{ x: number; y: number } | null>(null)
// Точка, относительно которой отрисована видимая подсказка: фиксируется в момент
// показа, поэтому дальнейшее движение мыши внутри активатора её не сдвигает
const anchorPosition = ref<{ x: number; y: number } | null>(null)

const onActivatorMouseEnter = (event: MouseEvent) => {
	if (props.followCursor) {
		cursorPosition.value = { x: event.clientX, y: event.clientY }
	}
	if (!props.showByActivatorClick) {
		showTooltip()
	}
}

const onActivatorMouseMove = (event: MouseEvent) => {
	if (!props.followCursor) {
		return
	}

	cursorPosition.value = { x: event.clientX, y: event.clientY }
}

const onActivatorMouseLeave = () => {
	cursorPosition.value = null

	if (!props.showByActivatorClick || props.closeOnActivatorMouseLeave) {
		hideTooltip()
	}
}

// Подсказка показана наведением. У такой курсор может уйти без mouseleave: активатор
// убирают из разметки, гасят ему pointer-events или перерисовывают список под курсором.
// Поэтому пока она видна, она сама сторожит курсор и нажатия. Подсказка по клику живёт
// по своему таймеру, её это не касается.
const shownByHover = ref(false)

const showTooltip = () => {
	if (props.disabled) {
		return
	}
	if (isMobile.value || isTablet.value) {
		return
	}
	if (delayTimer.value) {
		clearTimeout(delayTimer.value)
	}

	delayTimer.value = setTimeout(async () => {
		delayTimer.value = null
		// За время задержки активатор мог уйти из разметки или потерять курсор. Показывать
		// подсказку уже не к чему, позиционировать её будет не от чего.
		if (!activatorRef.value?.isConnected || !activatorRef.value.matches(':hover')) {
			return
		}
		anchorPosition.value = cursorPosition.value
		shownByHover.value = true
		isVisible.value = true
		await nextTick()
		updatePosition()
	}, props.delay)
}

const hideTooltip = () => {
	if (delayTimer.value) {
		clearTimeout(delayTimer.value)
		delayTimer.value = null
	}
	if (closeTimer.value) {
		clearTimeout(closeTimer.value)
		closeTimer.value = null
	}

	anchorPosition.value = null
	shownByHover.value = false
	isVisible.value = false
}

const showByClick = async () => {
	if (isMobile.value || isTablet.value) {
		return
	}
	anchorPosition.value = cursorPosition.value
	shownByHover.value = false
	isVisible.value = true
	await nextTick()
	updatePosition()
	if (!props.closeOnActivatorMouseLeave) {
		if (closeTimer.value) {
			clearTimeout(closeTimer.value)
		}
		closeTimer.value = setTimeout(() => hideTooltip(), props.closeByClickDelay)
	}
}

// Проверить, помещается ли тултип с указанной стороны
const checkPositionFit = (
	position: TooltipPosition,
	top: number,
	left: number,
	tooltipRect: DOMRect
): boolean => {
	const viewportWidth = window.innerWidth
	const viewportHeight = window.innerHeight
	const minMargin = 10 // минимальный отступ от края экрана

	switch (position) {
		case 'top':
			return top >= minMargin
		case 'bottom':
			return top + tooltipRect.height <= viewportHeight - minMargin
		case 'left':
			return left >= minMargin
		case 'right':
			return left + tooltipRect.width <= viewportWidth - minMargin
		default:
			return true
	}
}

// Получить противоположную позицию
const getOppositePosition = (position: TooltipPosition): TooltipPosition => {
	switch (position) {
		case 'top': return 'bottom'
		case 'bottom': return 'top'
		case 'left': return 'right'
		case 'right': return 'left'
	}
}

// Прямоугольник-якорь: в режиме followCursor это точка курсора, зафиксированная
// в момент показа, иначе — реальные границы активатора
const getAnchorRect = (): TooltipAnchorRect | null => {
	if (props.followCursor && anchorPosition.value) {
		const { x, y } = anchorPosition.value

		return { top: y, bottom: y, left: x, right: x, width: 0, height: 0 }
	}

	return activatorRef.value?.getBoundingClientRect() ?? null
}

const getHorizontalPosition = (
	activatorRect: TooltipAnchorRect,
	tooltipWidth: number,
	scrollX: number
) => {
	if (props.anchorAlign === 'start') {
		return activatorRect.left + scrollX
	}
	if (props.anchorAlign === 'end') {
		return activatorRect.right + scrollX - tooltipWidth
	}
	return activatorRect.left + scrollX + activatorRect.width / 2 - tooltipWidth / 2
}

// Обновить позицию тултипа
const updatePosition = () => {
	const activatorRect = getAnchorRect()

	if (!activatorRect || !tooltipRef.value) {
		return
	}

	const tooltipRect = tooltipRef.value.getBoundingClientRect()
	const scrollY = window.pageYOffset || document.documentElement.scrollTop
	const scrollX = window.pageXOffset || document.documentElement.scrollLeft

	// Пытаемся разместить тултип в запрошенной позиции
	let position = props.position
	let top = 0
	let left = 0
	const spacing = props.offset ?? (props.followCursor ? 16 : 8)

	// Рассчитываем идеальную позицию (по центру)
	switch (position) {
		case 'top':
			top = activatorRect.top + scrollY - tooltipRect.height - spacing
			left = getHorizontalPosition(activatorRect, tooltipRect.width, scrollX)
			break
		case 'bottom':
			top = activatorRect.bottom + scrollY + spacing
			left = getHorizontalPosition(activatorRect, tooltipRect.width, scrollX)
			break
		case 'left':
			top = activatorRect.top + scrollY + activatorRect.height / 2 - tooltipRect.height / 2
			left = activatorRect.left + scrollX - tooltipRect.width - spacing
			break
		case 'right':
			top = activatorRect.top + scrollY + activatorRect.height / 2 - tooltipRect.height / 2
			left = activatorRect.right + scrollX + spacing
			break
	}

	// Проверяем, помещается ли тултип в этой позиции
	const fitsOriginalPosition = checkPositionFit(
		position,
		top,
		left,
		tooltipRect
	)

	// Если не помещается, используем противоположную позицию
	if (!fitsOriginalPosition) {
		position = getOppositePosition(position)

		// Пересчитываем для противоположной позиции
		switch (position) {
			case 'top':
				top = activatorRect.top + scrollY - tooltipRect.height - spacing
				left = getHorizontalPosition(activatorRect, tooltipRect.width, scrollX)
				break
			case 'bottom':
				top = activatorRect.bottom + scrollY + spacing
				left = getHorizontalPosition(activatorRect, tooltipRect.width, scrollX)
				break
			case 'left':
				top = activatorRect.top + scrollY + activatorRect.height / 2 - tooltipRect.height / 2
				left = activatorRect.left + scrollX - tooltipRect.width - spacing
				break
			case 'right':
				top = activatorRect.top + scrollY + activatorRect.height / 2 - tooltipRect.height / 2
				left = activatorRect.right + scrollX + spacing
				break
		}
	}

	// Обновляем вычисленную позицию для отображения правильной стрелки
	computedPosition.value = position

	// Корректировка для предотвращения выхода за границы экрана
	const viewportWidth = window.innerWidth
	const viewportHeight = window.innerHeight
	const minMargin = 10

	// Сохраняем идеальную позицию для расчета смещения стрелки
	const idealLeft = left
	const idealTop = top

	// Корректировка по горизонтали
	if (left < minMargin) {
		left = minMargin
	}
	if (left + tooltipRect.width > viewportWidth - minMargin) {
		left = viewportWidth - tooltipRect.width - minMargin
	}

	// Корректировка по вертикали (если все равно не помещается даже в противоположной позиции)
	if (top < minMargin) {
		top = minMargin
	}
	if (top + tooltipRect.height > viewportHeight - minMargin) {
		top = viewportHeight - tooltipRect.height - minMargin
	}

	// Рассчитываем смещение стрелки
	arrowOffset.value = 0

	if (computedPosition.value === 'top' || computedPosition.value === 'bottom') {
		// Идеальное положение стрелки - по центру тултипа (50%)
		// Но если тултип смещен, нужно сместить стрелку относительно центра
		const idealArrowPosition = 50 // 50% от ширины тултипа

		// Вычисляем, насколько сместился тултип относительно идеальной позиции
		const tooltipShiftX = left - idealLeft

		// Вычисляем новую позицию стрелки
		let arrowPosition = idealArrowPosition - (tooltipShiftX / tooltipRect.width * 100)

		// Ограничиваем позицию стрелки, чтобы она не выходила за пределы тултипа
		const minArrowPosition = 15 // минимальный отступ от края тултипа
		const maxArrowPosition = 85 // максимальный отступ от края тултипа

		arrowPosition = Math.max(minArrowPosition, Math.min(maxArrowPosition, arrowPosition))

		arrowOffset.value = arrowPosition - 50 // сохраняем смещение от центра

	} else if (computedPosition.value === 'left' || computedPosition.value === 'right') {
		const idealArrowPosition = 50 // 50% от высоты тултипа
		const tooltipShiftY = top - idealTop

		let arrowPosition = idealArrowPosition - (tooltipShiftY / tooltipRect.height * 100)

		const minArrowPosition = 15
		const maxArrowPosition = 85

		arrowPosition = Math.max(minArrowPosition, Math.min(maxArrowPosition, arrowPosition))

		arrowOffset.value = arrowPosition - 50
	}

	tooltipPosition.value = { top, left }
}

const tooltipStyles = computed<StyleValue>(() => ({
	'max-width': props.maxWidth,
	'text-align': props.textAlign,
	top: `${tooltipPosition.value.top}px`,
	left: `${tooltipPosition.value.left}px`,
}))

const arrowStyles = computed<StyleValue>(() => {
	const styles: any = {}

	if (computedPosition.value === 'top' || computedPosition.value === 'bottom') {
		styles.left = `calc(50% + ${arrowOffset.value}%)`
	} else if (computedPosition.value === 'left' || computedPosition.value === 'right') {
		styles.top = `calc(50% + ${arrowOffset.value}%)`
	}

	return styles
})

const handleResize = () => {
	if (isVisible.value) {
		updatePosition()
	}
}

const handleScroll = () => {
	if (isVisible.value) {
		updatePosition()
	}
}

// Нажатие где угодно гасит подсказку по наведению: курсор занят делом, подсказка уже лишняя
const handleDocumentPointerDown = () => {
	if (shownByHover.value) {
		hideTooltip()
	}
}

// Сторож курсора для подсказки по наведению. Пока она видна, курсор обязан оставаться
// на активаторе; если активатора уже нет в разметке или указатель ушёл мимо mouseleave,
// подсказку снимаем здесь.
const handleDocumentMouseMove = (event: MouseEvent) => {
	if (!shownByHover.value) {
		return
	}
	const activator = activatorRef.value
	if (activator?.isConnected && activator.contains(event.target as Node)) {
		return
	}
	hideTooltip()
}

// Скрыть тултип (и отменить отложенный показ), если его отключили во время наведения.
// Если включили обратно, а курсор всё ещё на активаторе — показать снова:
// нативный mouseenter при возврате курсора с дочернего элемента повторно не срабатывает
watch(() => props.disabled, (isDisabled) => {
	if (isDisabled) {
		hideTooltip()
	} else if (activatorRef.value?.matches(':hover')) {
		showTooltip()
	}
})

watch(isVisible, (newValue) => {
	if (newValue) {
		window.addEventListener('resize', handleResize)
		window.addEventListener('scroll', handleScroll, true)
		document.addEventListener('pointerdown', handleDocumentPointerDown, true)
		document.addEventListener('mousemove', handleDocumentMouseMove, true)
		updatePosition()
	} else {
		window.removeEventListener('resize', handleResize)
		window.removeEventListener('scroll', handleScroll, true)
		document.removeEventListener('pointerdown', handleDocumentPointerDown, true)
		document.removeEventListener('mousemove', handleDocumentMouseMove, true)
	}
})

onUnmounted(() => {
	if (delayTimer.value) {
		clearTimeout(delayTimer.value)
	}
	if (closeTimer.value) {
		clearTimeout(closeTimer.value)
	}

	window.removeEventListener('resize', handleResize)
	window.removeEventListener('scroll', handleScroll, true)
	document.removeEventListener('pointerdown', handleDocumentPointerDown, true)
	document.removeEventListener('mousemove', handleDocumentMouseMove, true)
})

onMounted(() => {
	if (isVisible.value) {
		updatePosition()
	}
})

__expose({ showByClick })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "app-tooltip__activator",
      ref_key: "activatorRef",
      ref: activatorRef,
      onMouseenter: onActivatorMouseEnter,
      onMousemove: onActivatorMouseMove,
      onMouseleave: onActivatorMouseLeave
    }, [
      _renderSlot(_ctx.$slots, "activator")
    ], 544),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isVisible.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            ref_key: "tooltipRef",
            ref: tooltipRef,
            class: _normalizeClass(["app-tooltip__tooltip", [
					`app-tooltip__tooltip--${computedPosition.value}`,
					{ 'app-tooltip__tooltip--with-arrow': _ctx.showArrow },
					{ 'app-tooltip__tooltip--wrap': isWidthLimited.value }
				]]),
            style: _normalizeStyle(tooltipStyles.value)
          }, [
            _createElementVNode("div", {
              class: "app-tooltip__tooltip-content",
              innerHTML: _ctx.text
            }, null, 8, _hoisted_2),
            (_ctx.showArrow)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass(["app-tooltip__tooltip-arrow", `app-tooltip__tooltip-arrow--${computedPosition.value}`]),
                  style: _normalizeStyle(arrowStyles.value)
                }, null, 6))
              : _createCommentVNode("", true)
          ], 6))
        : _createCommentVNode("", true)
    ]))
  ]))
}
}

})